import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Logo from "@/components/Logo";

/**
 * Shared shell for legal / info pages (Privacy, Terms, Contact).
 * Ported from Lovable's InfoPageLayout, adapted to our web design language.
 */
export default function InfoPageLayout({ eyebrow, title, intro, updated, children }) {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border">
        <div className="container mx-auto flex items-center justify-between px-6 py-5">
          <Link to="/" className="inline-flex items-center gap-2" data-testid="info-back-home">
            <Logo size="sm" />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="container mx-auto px-6 pt-14 pb-8">
        <div className="max-w-3xl">
          {eyebrow && (
            <p className="mb-3 text-sm font-semibold uppercase tracking-widest text-primary">
              {eyebrow}
            </p>
          )}
          <h1 className="font-heading text-4xl font-extrabold tracking-tight sm:text-5xl">
            {title}
          </h1>
          {intro && (
            <p className="mt-5 max-w-2xl text-lg text-muted-foreground">{intro}</p>
          )}
          {updated && (
            <p className="mt-4 text-sm text-muted-foreground">Last updated {updated}</p>
          )}
        </div>
      </section>

      {/* Content */}
      <main className="container mx-auto px-6 pb-24">
        <div className="max-w-3xl space-y-10">{children}</div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-8">
        <div className="container mx-auto flex flex-col items-center justify-between gap-4 px-6 text-sm text-muted-foreground md:flex-row">
          <p>© {new Date().getFullYear()} GuardianNest. All rights reserved.</p>
          <div className="flex flex-wrap gap-4 gap-y-2 sm:gap-6">
            <Link to="/security" className="hover:text-foreground" data-testid="info-footer-security">Security</Link>
            <Link to="/privacy" className="hover:text-foreground" data-testid="info-footer-privacy">Privacy</Link>
            <Link to="/terms" className="hover:text-foreground" data-testid="info-footer-terms">Terms</Link>
            <Link to="/contact" className="hover:text-foreground" data-testid="info-footer-contact">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export function InfoSection({ title, children }) {
  return (
    <section>
      <h2 className="font-heading text-xl font-bold text-foreground">{title}</h2>
      <div className="mt-3 space-y-4 text-base leading-relaxed text-muted-foreground">
        {children}
      </div>
    </section>
  );
}
